import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Drawer } from '@/components/ui/Drawer';
import { LoadingState } from '@/components/ui/LoadingState';
import { apiErrorMessage } from '@/services/apiClient';
import { useCommunicationQuery } from '../api/crm.api';
import { AttemptStatusBadge, CommunicationStatusBadge } from './communicationLabels';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5">
      <dt className="text-muted col-span-1 text-sm">{label}</dt>
      <dd className="col-span-2 text-sm">{children}</dd>
    </div>
  );
}

/**
 * One message, in full.
 *
 * Everything here comes from the message's own record. There is no provider payload, no JID, no
 * session state and no raw error — the API does not return them and this screen could not show them
 * if it wanted to. `safeFailureCode` is a mapped word, which is what makes it safe to print.
 */
export function CommunicationDetailDrawer({
  communicationId,
  onClose,
}: {
  communicationId: string | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data, isPending, isError, error } = useCommunicationQuery(communicationId);

  if (!communicationId) return null;

  const when = (value: string | null): string =>
    value ? new Date(value).toLocaleString(i18n.language) : '—';

  return (
    <Drawer isOpen onClose={onClose} title={t('crm.detail.title')}>
      {isError ? (
        <Alert tone="error">{apiErrorMessage(error) ?? t('crm.detail.loadError')}</Alert>
      ) : isPending || !data ? (
        <LoadingState />
      ) : (
        <div className="space-y-5">
          <dl className="divide-line divide-y">
            <Row label={t('crm.fields.customer')}>
              <Link className="text-brand underline" to={`/customers/${data.customerId}`}>
                <span dir="auto">{data.customerName}</span>
              </Link>
            </Row>
            <Row label={t('crm.fields.branch')}>
              <span dir="auto">{data.branchName}</span>
            </Row>
            <Row label={t('crm.fields.purpose')}>{t(`crm.purpose.${data.purpose}`)}</Row>
            <Row label={t('crm.fields.channel')}>{t(`crm.channel.${data.channel}`)}</Row>
            <Row label={t('crm.fields.deliveryMode')}>
              {t(`crm.deliveryMode.${data.deliveryMode}`)}
            </Row>
            <Row label={t('crm.fields.language')}>{t(`crm.language.${data.locale}`)}</Row>
            <Row label={t('crm.fields.status')}>
              <CommunicationStatusBadge status={data.status} />
            </Row>
            <Row label={t('crm.fields.preparedAt')}>{when(data.preparedAt)}</Row>
          </dl>

          {/* Why nothing was sent, in the platform's own words. Not a failure: a decision. */}
          {data.suppressionReason ? (
            <Alert tone="info">
              <p className="font-medium">{t('crm.detail.suppressedTitle')}</p>
              <p>{t(`crm.suppression.${data.suppressionReason}`)}</p>
            </Alert>
          ) : null}

          {data.failureCode ? (
            <Alert tone="error">
              <p className="font-medium">{t('crm.detail.failedTitle')}</p>
              <p>{t(`crm.failure.${data.failureCode}`, { defaultValue: data.failureCode })}</p>
            </Alert>
          ) : null}

          <section>
            <h3 className="mb-2 text-sm font-semibold">{t('crm.detail.message')}</h3>
            {data.bodySnapshot ? (
              <>
                <p
                  className="bg-canvas rounded-lg p-3 text-sm whitespace-pre-wrap"
                  dir={data.locale === 'AR' ? 'rtl' : 'ltr'}
                >
                  {data.bodySnapshot}
                </p>
                <p className="text-muted mt-2 text-xs">{t('crm.detail.frozenBody')}</p>
              </>
            ) : (
              <p className="text-muted text-sm">{t('crm.detail.noBody')}</p>
            )}
          </section>

          <dl className="divide-line divide-y">
            <Row label={t('crm.fields.template')}>
              {data.templateId ? (
                <span>
                  <Link className="text-brand underline" to="/crm/templates">
                    {t('crm.detail.templateLink')}
                  </Link>{' '}
                  <span className="text-muted">
                    {t('crm.detail.version', { version: data.templateVersion ?? 0 })}
                  </span>
                </span>
              ) : (
                t('crm.detail.noTemplate')
              )}
            </Row>
            {data.opticalJobId ? (
              <Row label={t('crm.fields.relatedJob')}>
                <Link className="text-brand underline" to={`/sales/orders/${data.salesOrderId}`}>
                  {t('crm.detail.openOrder')}
                </Link>
              </Row>
            ) : null}
          </dl>

          <section>
            <h3 className="mb-2 text-sm font-semibold">{t('crm.detail.attempts')}</h3>
            {data.attempts.length === 0 ? (
              <p className="text-muted text-sm">{t('crm.detail.noAttempts')}</p>
            ) : (
              <ol className="space-y-3">
                {data.attempts.map((attempt) => (
                  <li key={attempt.id} className="border-line rounded-lg border p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {t('crm.detail.attemptNumber', { number: attempt.attemptNumber })}
                      </span>
                      <AttemptStatusBadge status={attempt.status} />
                    </div>
                    <dl className="text-muted space-y-1 text-xs">
                      <div className="flex justify-between gap-2">
                        <dt>{t('crm.fields.sender')}</dt>
                        <dd>
                          <Badge tone="neutral">{t(`crm.identity.${attempt.identitySlot}`)}</Badge>{' '}
                          <span dir="ltr">{attempt.senderPhoneSnapshot ?? '—'}</span>
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt>{t('crm.fields.recipient')}</dt>
                        <dd dir="ltr">{attempt.recipientSnapshot ?? '—'}</dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt>{t('crm.fields.startedAt')}</dt>
                        <dd>{when(attempt.startedAt)}</dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt>{t('crm.fields.acceptedAt')}</dt>
                        <dd>{when(attempt.providerAcceptedAt)}</dd>
                      </div>
                      {attempt.safeFailureCode ? (
                        <div className="flex justify-between gap-2">
                          <dt>{t('crm.fields.reason')}</dt>
                          <dd>
                            {t(`crm.failure.${attempt.safeFailureCode}`, {
                              defaultValue: attempt.safeFailureCode,
                            })}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </li>
                ))}
              </ol>
            )}
            <p className="text-muted mt-2 text-xs">{t('crm.detail.attemptsHistorical')}</p>
          </section>
        </div>
      )}
    </Drawer>
  );
}
